// ─── WOT Realm Definitions ─────────────────────────
// Single source of truth for all 10 knowledge realms.
// Used by both client (display, colors) and server (question generation).

export interface Realm {
  id: RealmId;
  name: string;
  nameTR: string;
  color: string;
  icon: string;         // SVG icon name or emoji placeholder
  description: string;
  descriptionTR: string;
  aiPrompt: string;     // Base prompt for AI question generation
  /** 1 easiest, 3 hardest. See CHALLENGE below before changing one. */
  challenge: 1 | 2 | 3;
}

// ─── On challenge ───────────────────────────────────────────────────
//
// Realms are not equally hard, and the difference is large enough that picking
// one is partly a difficulty choice. Measured on 2026-08-16 from real play:
// 3,025 answers across 2,120 questions, restricted to the EASY band so that a
// realm serving more hard questions could not look hard by accident.
//
//   ENIGMA 0.40  COSMOS 0.44  ARENA 0.44  SOUNDWAVE 0.46   -> 3
//   EUREKA 0.50  FEAST 0.52   CHRONICLES 0.54              -> 2
//   NEXUS 0.61   ATLAS 0.63   SPOTLIGHT 0.66               -> 1
//
// Twenty six points separate the ends. The splits sit on the two natural gaps
// in that list, at 0.46/0.50 and 0.54/0.61, rather than on round numbers.
//
// This is a snapshot, not a live figure. The sample came from a small tester
// group, and retiring or adding questions moves it. Re-measure before trusting
// it again; the query is the times_correct over times_served ratio per realm
// with difficulty = 'easy'.
//
// Note that the numbers contradicted the obvious guesses: SOUNDWAVE plays hard
// and EUREKA does not, which is the reason for measuring rather than assigning
// by how specialised a subject sounds.

export const REALM_IDS = [
  'ARENA', 'EUREKA', 'CHRONICLES', 'ATLAS', 'SPOTLIGHT',
  'SOUNDWAVE', 'FEAST', 'COSMOS', 'ENIGMA', 'NEXUS',
] as const;

export type RealmId = (typeof REALM_IDS)[number];

export const REALMS: Record<RealmId, Realm> = {
  ARENA: {
    id: 'ARENA',
    name: 'Sports & Athletics',
    nameTR: 'Spor & Atletizm',
    color: '#EF4444',
    icon: 'arena',
    description: 'Football, Olympics, basketball, tennis, and athletic records',
    descriptionTR: 'Futbol, Olimpiyatlar, basketbol, tenis ve atletizm rekorları',
    aiPrompt: 'Cover a massive, diverse athletics spectrum: Olympic history, extreme sports, global football leagues, legendary tennis matches, and forgotten sporting traditions.',
    challenge: 3,
  },
  EUREKA: {
    id: 'EUREKA',
    name: 'Science & Discovery',
    nameTR: 'Bilim & Keşif',
    color: '#06B6D4',
    icon: 'eureka',
    description: 'Physics, chemistry, biology, inventions, and scientific breakthroughs',
    descriptionTR: 'Fizik, kimya, biyoloji, icatlar ve bilimsel keşifler',
    aiPrompt: 'Cover a massive, diverse science spectrum: Quantum physics milestones, famous biology experiments, obscure chemical elements, ancient inventions, and Nobel prize anomalies.',
    challenge: 2,
  },
  CHRONICLES: {
    id: 'CHRONICLES',
    // Kept short so it fits beside the realm code on a phone, and parallel with
    // ATLAS. The longer framing lives in description, which has room for it.
    name: 'World History',
    nameTR: 'Dünya Tarihi',
    color: '#F59E0B',
    icon: 'chronicles',
    description: 'Ancient empires, wars, revolutions, and historical figures',
    descriptionTR: 'Antik imparatorluklar, savaşlar, devrimler ve tarihi figürler',
    aiPrompt: 'Cover a massive, diverse history spectrum: Mesoamerican civilizations, obscure Asian dynasties, industrial revolution figures, ancient battles, and forgotten political treaties.',
    challenge: 2,
  },
  ATLAS: {
    id: 'ATLAS',
    name: 'World Geography',
    nameTR: 'Dünya Coğrafyası',
    color: '#10B981',
    icon: 'atlas',
    description: 'Countries, capitals, landmarks, oceans, and natural wonders',
    descriptionTR: 'Ülkeler, başkentler, simgeler, okyanuslar ve doğa harikaları',
    aiPrompt: 'Cover a massive, diverse geography spectrum: Uninhabited islands, deepest oceanic trenches, obscure country capitals, indigenous geographical features, and extreme climates.',
    challenge: 1,
  },
  SPOTLIGHT: {
    id: 'SPOTLIGHT',
    name: 'Cinema & Stage',
    nameTR: 'Sinema & Sahne',
    color: '#FACC15',
    icon: 'spotlight',
    description: 'Movies, TV series, directors, actors, and award ceremonies',
    descriptionTR: 'Filmler, diziler, yönetmenler, aktörler ve ödül törenleri',
    aiPrompt: 'Cover a massive, diverse cinema spectrum: European arthouse films, golden age Hollywood trivia, legendary directors, behind-the-scenes movie stunts, and iconic costume designs.',
    challenge: 1,
  },
  SOUNDWAVE: {
    id: 'SOUNDWAVE',
    name: 'Music & Rhythm',
    nameTR: 'Müzik & Ritim',
    color: '#EC4899',
    icon: 'soundwave',
    description: 'Artists, albums, genres, music history, and iconic songs',
    descriptionTR: 'Sanatçılar, albümler, müzik türleri, müzik tarihi ve ikonik şarkılar',
    aiPrompt: 'Cover a massive, diverse musical spectrum: Classical music composers, 90s pop icons, jazz legends, specific global instruments (e.g. sitar, koto), and legendary underground rock bands.',
    challenge: 3,
  },
  FEAST: {
    id: 'FEAST',
    name: 'World Cuisine',
    nameTR: 'Dünya Mutfağı',
    color: '#F97316',
    icon: 'feast',
    description: 'Traditional dishes, ingredients, cooking techniques, and food culture',
    descriptionTR: 'Geleneksel yemekler, malzemeler, pişirme teknikleri ve yemek kültürü',
    aiPrompt: 'Cover a massive, diverse culinary spectrum: Rare tropical fruits, historical fermentation techniques, bizarre Michelin-star ingredients, street foods of Asia, and obscure spices.',
    challenge: 2,
  },
  COSMOS: {
    id: 'COSMOS',
    name: 'Space & Universe',
    nameTR: 'Uzay & Evren',
    color: '#3B82F6',
    icon: 'cosmos',
    description: 'Planets, stars, space missions, black holes, and the universe',
    descriptionTR: 'Gezegenler, yıldızlar, uzay görevleri, kara delikler ve evren',
    aiPrompt: 'Cover a massive, diverse space spectrum: Exoplanets, early Soviet space missions, astrophysics theories (quasars/pulsars), telescope discoveries, and prominent 17th-century astronomers.',
    challenge: 3,
  },
  ENIGMA: {
    id: 'ENIGMA',
    name: 'Myths & Mysteries',
    nameTR: 'Mitler & Gizemler',
    color: '#D97706',
    icon: 'enigma',
    description: 'Mythology, legends, folklore, unsolved mysteries, and ancient beliefs',
    descriptionTR: 'Mitoloji, efsaneler, folklor, çözülmemiş gizemler ve antik inançlar',
    aiPrompt: 'Cover a massive, diverse mythology spectrum: Norse/Egyptian deities, urban legends from Japan, cryptids like the Chupacabra, unsolved historical disappearances, and Aztec folklore.',
    challenge: 3,
  },
  NEXUS: {
    id: 'NEXUS',
    name: 'Technology & Digital',
    nameTR: 'Teknoloji & Dijital',
    color: '#2DD4BF',
    icon: 'nexus',
    description: 'AI, programming, internet culture, gaming, and tech breakthroughs',
    descriptionTR: 'Yapay zeka, programlama, internet kültürü, oyunlar ve teknolojik atılımlar',
    aiPrompt: 'Cover a massive, diverse technology spectrum: Retro arcade hardware, early internet protocols, obscure programming languages, cybersecurity milestones, and hardware anomalies.',
    challenge: 1,
  },
} as const;

// Utility: Get realm by ID
export function getRealm(id: RealmId): Realm {
  return REALMS[id];
}

// Utility: Get all realm IDs as array
export function getAllRealmIds(): RealmId[] {
  return [...REALM_IDS];
}

// Utility: Get realm color by ID  
export function getRealmColor(id: RealmId): string {
  return REALMS[id].color;
}
